import { useEffect, useState } from 'react';
import { r, s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { DropdownFiltersPopover } from '@/ui/composites/filters/DropdownFiltersPopover';
import { SearchFilterField } from '@/ui/composites/filters/SearchFilterField';
import {
  Box,
  ButtonBase,
  FilterListOutlinedIcon,
  IconButton,
  Popover,
  SearchIcon,
  Text,
  Tooltip,
  useTheme,
} from '@/ui/primitives';
import { useMediaQuery } from '@/ui/primitives/hooks';
import {
  buildToolbarIconButtonSx,
  buildToolbarIconCircleSx,
  toolbarControlRadius,
} from '../tableSystem/tableToolbar.helpers';
import {
  normaliseMetricsToolbarTagOptions,
  type MetricsToolbarTagOption,
} from './metricsToolbarTagOptions';
import type { TableDropdownOption, TableStatusOption } from '../tableSystem/tableToolbar.types';
import type { ReactElement, RefObject } from 'react';

type MetricsToolbarCollapsedControlsProps<TStatus extends string> = {
  filtersOpen: boolean;
  filtersAnchorEl: HTMLElement | null;
  onOpenFilters: (event: React.MouseEvent<HTMLElement>) => void;
  onCloseFilters: () => void;
  searchValue: string;
  searchAriaLabel: string;
  setSearchValue: (value: string) => void;
  searchInputRef: RefObject<HTMLInputElement | null>;
  handleSearchBlur: () => void;
  handleSearchKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  hasActiveFilters: boolean;
  statusOptions: Array<TableStatusOption<TStatus>>;
  statusFilterLabel: string;
  filterStatuses: TStatus[];
  onFilterStatusesChange: (next: TStatus[]) => void;
  filterEntities: string[];
  onFilterEntitiesChange?: (next: string[]) => void;
  entityOptions?: TableDropdownOption[];
  entityLabel?: string;
  filterItems: string[];
  onFilterItemsChange?: (next: string[]) => void;
  itemOptions?: TableDropdownOption[];
  itemLabel?: string;
  filterSuites: string[];
  onFilterSuitesChange?: (next: string[]) => void;
  suiteOptions?: TableDropdownOption[];
  suiteLabel?: string;
  filterTags: string[];
  onFilterTagsChange: (next: string[]) => void;
  availableTags: MetricsToolbarTagOption[];
  filterProjects?: string[];
  onFilterProjectsChange?: (next: string[]) => void;
  projectOptions?: TableDropdownOption[];
  preserveWindowScroll: (action: () => void) => void;
  toolbarInactiveIconColor: string;
  tooltipSlotProps: {
    tooltip: {
      sx: {
        fontSize: string;
        lineHeight: number;
      };
    };
  };
};

export const MetricsToolbarCollapsedControls = <TStatus extends string>({
  filtersOpen,
  filtersAnchorEl,
  onOpenFilters,
  onCloseFilters,
  searchValue,
  searchAriaLabel,
  setSearchValue,
  searchInputRef,
  handleSearchBlur,
  handleSearchKeyDown,
  hasActiveFilters,
  statusOptions,
  statusFilterLabel,
  filterStatuses,
  onFilterStatusesChange,
  filterEntities,
  onFilterEntitiesChange,
  entityOptions,
  entityLabel,
  filterItems,
  onFilterItemsChange,
  itemOptions,
  itemLabel,
  filterSuites,
  onFilterSuitesChange,
  suiteOptions,
  suiteLabel,
  filterTags,
  onFilterTagsChange,
  availableTags,
  filterProjects,
  onFilterProjectsChange,
  projectOptions,
  preserveWindowScroll,
  toolbarInactiveIconColor,
  tooltipSlotProps,
}: MetricsToolbarCollapsedControlsProps<TStatus>): ReactElement => {
  const theme = useTheme();
  const toolbarFieldBackgroundColor = 'var(--surface-page)';
  const hideLabels = useMediaQuery(theme.breakpoints.down('md'));
  const toolbarControlSize = 32;
  const condensedInactiveIconColor = 'common.white';
  const toolbarIconButtonSx = buildToolbarIconButtonSx(
    toolbarInactiveIconColor,
    toolbarControlSize,
  );
  const labeledToolbarButtonSx = {
    height: `${toolbarControlSize}px`,
    minHeight: `${toolbarControlSize}px`,
    px: s('sm'),
    borderRadius: toolbarControlRadius,
    display: 'inline-flex',
    alignItems: 'center',
    gap: s('xs'),
    lineHeight: 0,
  } as const;
  const iconCircleSx = {
    width: 30,
    height: 30,
    minWidth: 30,
    minHeight: 30,
    maxWidth: 30,
    maxHeight: 30,
    flex: '0 0 30px',
    aspectRatio: '1 / 1',
    borderRadius: r('pill'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as const;
  const [searchPopoverAnchorEl, setSearchPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const tagOptions = normaliseMetricsToolbarTagOptions(availableTags);

  useEffect(() => {
    if (searchPopoverAnchorEl === null) return;
    window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  }, [searchInputRef, searchPopoverAnchorEl]);

  return (
    <>
      {hideLabels ? (
        <>
          <Tooltip title="Filters" arrow enterDelay={250} slotProps={tooltipSlotProps}>
            <IconButton
              size="medium"
              aria-label="Open filters"
              onClick={onOpenFilters}
              sx={[
                toolbarIconButtonSx,
                { color: hasActiveFilters ? 'primary.main' : condensedInactiveIconColor },
              ]}
            >
              <Box sx={buildToolbarIconCircleSx(false)}>
                <FilterListOutlinedIcon
                  sx={{
                    color: hasActiveFilters ? 'primary.main' : condensedInactiveIconColor,
                    width: '22px !important',
                    height: '22px !important',
                  }}
                />
              </Box>
            </IconButton>
          </Tooltip>
          <Tooltip title="Search" arrow enterDelay={250} slotProps={tooltipSlotProps}>
            <IconButton
              size="medium"
              aria-label="Open search"
              onClick={(event) => {
                setSearchPopoverAnchorEl(event.currentTarget);
              }}
              sx={[
                toolbarIconButtonSx,
                {
                  color: searchValue.trim().length > 0 ? 'primary.main' : toolbarInactiveIconColor,
                },
              ]}
            >
              <Box sx={buildToolbarIconCircleSx(false)}>
                <SearchIcon
                  sx={{
                    color:
                      searchValue.trim().length > 0 ? 'primary.main' : toolbarInactiveIconColor,
                    width: '22px !important',
                    height: '22px !important',
                  }}
                />
              </Box>
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <>
          <ButtonBase
            aria-label="Open filters"
            onClick={onOpenFilters}
            sx={[
              labeledToolbarButtonSx,
              {
                color: hasActiveFilters ? 'primary.main' : condensedInactiveIconColor,
              },
            ]}
          >
            <Box sx={iconCircleSx}>
              <FilterListOutlinedIcon
                sx={{
                  color: hasActiveFilters ? 'primary.main' : condensedInactiveIconColor,
                  width: '22px !important',
                  height: '22px !important',
                }}
              />
            </Box>
            <Text
              role="label"
              component="span"
              transform="none"
              sx={{
                fontSize: (muiTheme) => muiTheme.typography.pxToRem(15),
                lineHeight: 1.2,
                fontWeight: (muiTheme) => muiTheme.typography.fontWeightRegular,
                color: 'inherit',
                whiteSpace: 'nowrap',
                pr: s('xxs'),
              }}
            >
              Filters
            </Text>
          </ButtonBase>
          <ButtonBase
            aria-label="Open search"
            onClick={(event) => {
              setSearchPopoverAnchorEl(event.currentTarget);
            }}
            sx={[
              labeledToolbarButtonSx,
              {
                color: searchValue.trim().length > 0 ? 'primary.main' : toolbarInactiveIconColor,
              },
            ]}
          >
            <Box sx={iconCircleSx}>
              <SearchIcon
                sx={{
                  color: searchValue.trim().length > 0 ? 'primary.main' : toolbarInactiveIconColor,
                  width: '22px !important',
                  height: '22px !important',
                }}
              />
            </Box>
            <Text
              role="label"
              component="span"
              transform="none"
              sx={{
                fontSize: (muiTheme) => muiTheme.typography.pxToRem(15),
                lineHeight: 1.2,
                fontWeight: (muiTheme) => muiTheme.typography.fontWeightRegular,
                color: 'inherit',
                whiteSpace: 'nowrap',
                pr: s('xxs'),
              }}
            >
              Search
            </Text>
          </ButtonBase>
        </>
      )}
      <DropdownFiltersPopover
        open={filtersOpen}
        anchorEl={filtersAnchorEl}
        onClose={onCloseFilters}
        selectedStatuses={filterStatuses}
        onStatusesChange={(next) => {
          preserveWindowScroll(() => {
            onFilterStatusesChange(next as TStatus[]);
          });
        }}
        statusOptions={statusOptions}
        statusLabel={statusFilterLabel}
        selectedEntities={filterEntities}
        onEntitiesChange={
          onFilterEntitiesChange
            ? (next) => {
                preserveWindowScroll(() => {
                  onFilterEntitiesChange(next);
                });
              }
            : undefined
        }
        entityOptions={entityOptions}
        entityLabel={entityLabel}
        selectedItems={filterItems}
        onItemsChange={
          onFilterItemsChange
            ? (next) => {
                preserveWindowScroll(() => {
                  onFilterItemsChange(next);
                });
              }
            : undefined
        }
        itemOptions={itemOptions}
        itemLabel={itemLabel}
        selectedSuites={filterSuites}
        onSuitesChange={
          onFilterSuitesChange
            ? (next) => {
                preserveWindowScroll(() => {
                  onFilterSuitesChange(next);
                });
              }
            : undefined
        }
        suiteOptions={suiteOptions}
        suiteLabel={suiteLabel}
        selectedTags={filterTags}
        onTagsChange={(next) => {
          preserveWindowScroll(() => {
            onFilterTagsChange(next);
          });
        }}
        tagOptions={tagOptions}
        selectedProjects={filterProjects}
        onProjectsChange={
          onFilterProjectsChange
            ? (next) => {
                preserveWindowScroll(() => {
                  onFilterProjectsChange(next);
                });
              }
            : undefined
        }
        projectOptions={projectOptions}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      />
      <Popover
        open={searchPopoverAnchorEl !== null}
        anchorEl={searchPopoverAnchorEl}
        onClose={() => {
          setSearchPopoverAnchorEl(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        paperSx={{
          backgroundColor: 'var(--surface-page)',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: `${toolbarControlRadius} !important`,
          p: s('none'),
          boxShadow: 'none',
          width: 'min(90vw, 280px)',
          overflow: 'hidden',
        }}
      >
        <Stack gap="md">
          <Box sx={{ px: s('lg'), py: s('lg') }}>
            <SearchFilterField
              value={searchValue}
              onChange={(event) => {
                setSearchValue(event.target.value);
              }}
              inputAriaLabel={searchAriaLabel}
              inputBackgroundColor={toolbarFieldBackgroundColor}
              controlHeight={32}
              compact
              leadingSearchAdornment={false}
              trailingSearchAdornment
              filledClearIcon={false}
              sx={{
                width: '100%',
                minWidth: 0,
                '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
                  borderRadius: `${toolbarControlRadius} !important`,
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: `${toolbarControlRadius} !important`,
                },
              }}
              slotProps={{
                htmlInput: {
                  ref: (instance: HTMLInputElement | null) => {
                    searchInputRef.current = instance;
                  },
                  onBlur: handleSearchBlur,
                  onKeyDown: handleSearchKeyDown,
                },
              }}
            />
          </Box>
        </Stack>
      </Popover>
    </>
  );
};
